import { create } from 'zustand';

// One shared array for every un-opened project: a fresh `[]` per read would
// give the selector a new reference each render and re-render forever.
const EMPTY_IDS: string[] = [];

interface ApprovalRailState {
  /** Ids of the branches the user has opened, per project. */
  expandedByProject: Record<string, string[]>;
  /** Hides the tree so the assets beside it get the full width. */
  isCollapsed: boolean;
}

/**
 * Which rail branches are open.
 *
 * Held outside the component because the rail is rendered by the folders list
 * and the folder detail pages, so React tears it down whenever navigation
 * crosses between them — and a tree that forgets what was open on every
 * selection is unusable.
 */
export const useApprovalRailStore = create<ApprovalRailState>(() => ({
  expandedByProject: {},
  isCollapsed: false,
}));

export const useIsRailCollapsed = (): boolean => useApprovalRailStore((state) => state.isCollapsed);

export const toggleRail = (): void => {
  useApprovalRailStore.setState((state) => ({ isCollapsed: !state.isCollapsed }));
};

export const useExpandedFolderIds = (projectId: string): string[] =>
  useApprovalRailStore((state) => state.expandedByProject[projectId] ?? EMPTY_IDS);

export const toggleExpandedFolder = (projectId: string, folderId: string): void => {
  useApprovalRailStore.setState((state) => {
    const open = state.expandedByProject[projectId] ?? EMPTY_IDS;
    return {
      expandedByProject: {
        ...state.expandedByProject,
        [projectId]: open.includes(folderId)
          ? open.filter((id) => id !== folderId)
          : [...open, folderId],
      },
    };
  });
};

/** Opens every branch on the way to a folder, so a deep link shows its place. */
export const expandFolderPath = (projectId: string, folderIds: string[]): void => {
  useApprovalRailStore.setState((state) => {
    const open = state.expandedByProject[projectId] ?? EMPTY_IDS;
    const missing = folderIds.filter((id) => !open.includes(id));
    if (missing.length === 0) return state;
    return {
      expandedByProject: { ...state.expandedByProject, [projectId]: [...open, ...missing] },
    };
  });
};
